import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, TextField, MenuItem,
  Alert, Snackbar, CircularProgress, Tabs, Tab, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Switch, FormControlLabel, Avatar,
  Pagination, Skeleton,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Visibility as VisibilityIcon, Refresh as RefreshIcon,
  Search as SearchIcon, CreditCard as CreditCardIcon,
  Close as CloseIcon, Person as PersonIcon, Email as EmailIcon,
  CalendarToday as CalendarIcon, Save as SaveIcon,
  OpenInNew as OpenIcon, WarningAmber as WarningIcon,
  Phone as PhoneIcon, Home as HomeIcon,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import { fmtDate, fmtDateTime } from '../utils/dateFormat';

const PRIMARY = '#00A7E3';

/** Inset panels: grey.50 is always light in our palette — breaks dark mode (light text on white). */
const adminInsetBoxSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: (t) => (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.09) : t.palette.grey[50]),
};
const CARD_SHADOW = '0 1px 3px rgba(0,0,0,0.08)';

const cardBase = {
  boxShadow: CARD_SHADOW, borderRadius: 1,
  border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper',
};
const sectionHeaderSx = {
  px: 3, py: 2.5, borderBottom: '1px solid', borderColor: 'divider',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
};
const tableHeadCellSx = {
  fontWeight: 600, py: 1.5, fontSize: '11px', color: 'text.secondary',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const QR_PAGE_SIZE = 7;
const NFC_PAGE_SIZE = 7;

function AdminNfcTableSkeletonRows({ rows = NFC_PAGE_SIZE }) {
  return [...Array(rows)].map((_, i) => (
    <TableRow key={`nfc-sk-${i}`}>
      <TableCell sx={{ py: 2 }}><Skeleton variant="rounded" width={120} height={24} animation="wave" sx={{ borderRadius: 1 }} /></TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={56} height={20} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="58%" height={20} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="72%" height={16} animation="wave" />
      </TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: '12px' }} animation="wave" /></TableCell>
      <TableCell align="center" sx={{ py: 2 }}><Skeleton variant="text" width={24} height={22} sx={{ mx: 'auto' }} animation="wave" /></TableCell>
      <TableCell align="right" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Skeleton variant="circular" width={34} height={34} animation="wave" />
          <Skeleton variant="circular" width={34} height={34} animation="wave" />
        </Box>
      </TableCell>
    </TableRow>
  ));
}

function AdminSubTransactionsSkeletonRows({ rows = 7 }) {
  return [...Array(rows)].map((_, i) => (
    <TableRow key={`sub-tx-sk-${i}`}>
      <TableCell sx={{ py: 1.5 }}>
        <Skeleton variant="text" width="58%" height={18} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="72%" height={14} animation="wave" />
      </TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="rounded" width={48} height={22} sx={{ borderRadius: '11px' }} animation="wave" /></TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="text" width={44} height={18} animation="wave" /></TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="text" width={56} height={18} animation="wave" /></TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="text" width={100} height={16} animation="wave" /></TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="text" width={72} height={16} animation="wave" /></TableCell>
      <TableCell sx={{ py: 1.5 }}><Skeleton variant="text" width={72} height={16} animation="wave" /></TableCell>
      <TableCell align="right" sx={{ py: 1.5 }}><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} animation="wave" /></TableCell>
    </TableRow>
  ));
}

function AdminQrTableSkeletonRows({ rows = QR_PAGE_SIZE }) {
  return [...Array(rows)].map((_, i) => (
    <TableRow key={`qr-sk-${i}`}>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="72%" height={20} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="40%" height={16} animation="wave" />
      </TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={140} height={20} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}>
        <Skeleton variant="text" width="55%" height={20} animation="wave" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="70%" height={16} animation="wave" />
      </TableCell>
      <TableCell align="center" sx={{ py: 2 }}><Skeleton variant="rounded" width={40} height={22} sx={{ mx: 'auto' }} animation="wave" /></TableCell>
      <TableCell align="center" sx={{ py: 2 }}><Skeleton variant="circular" width={10} height={10} sx={{ mx: 'auto' }} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={72} height={18} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="circular" width={28} height={28} animation="wave" /></TableCell>
    </TableRow>
  ));
}

function AdminUserTableSkeletonRows({ rows = 7 }) {
  return [...Array(rows)].map((_, i) => (
    <TableRow key={`user-sk-${i}`}>
      <TableCell sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="circular" width={36} height={36} animation="wave" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width="55%" height={22} sx={{ mb: 0.5 }} animation="wave" />
            <Skeleton variant="text" width="72%" height={18} animation="wave" />
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="rounded" width={52} height={24} sx={{ borderRadius: '12px' }} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={72} height={22} animation="wave" /></TableCell>
      <TableCell align="center" sx={{ py: 2 }}><Skeleton variant="text" width={28} height={22} sx={{ mx: 'auto' }} animation="wave" /></TableCell>
      <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={96} height={22} animation="wave" /></TableCell>
      <TableCell align="right" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Skeleton variant="circular" width={34} height={34} animation="wave" />
          <Skeleton variant="circular" width={34} height={34} animation="wave" />
          <Skeleton variant="circular" width={34} height={34} animation="wave" />
        </Box>
      </TableCell>
    </TableRow>
  ));
}

const API = 'https://api.webcard.digital/api';
const USER_PAGE_SIZE = 7;
/** When totalPages > 1, keeps table area height stable on short last pages (Users, NFC, QR — default table density). */
const ADMIN_PAGINATED_TABLE_MIN_PX = 52 + USER_PAGE_SIZE * 74;
/** Same idea for All Transactions (`Table size="small"`); shorter rows than user/NFC/QR tables. */
const ADMIN_SUB_TXN_TABLE_MIN_PX = 46 + USER_PAGE_SIZE * 46;
const getToken = () => localStorage.getItem('token');
const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

const EMPTY_USER_FORM = { firstName: '', lastName: '', email: '', password: '', role: 'user' };

const SUBSCRIPTION_FILTER_STATUSES = ['active', 'created', 'cancelled', 'halted', 'expired', 'failed', 'paid'];

function subscriptionStatusFilterLabel(value) {
  if (value === '' || value == null) return 'All Status';
  return String(value).charAt(0).toUpperCase() + String(value).slice(1);
}

/** Display label for a plan code (users.plan or subscriptions.plan_type / order plan_type). */
function formatUserPlanLabel(plan) {
  if (!plan || plan === 'free') return 'Free';
  if (plan === 'lifetime') return 'Lifetime Access';
  if (plan === 'premium') return 'Pro';
  if (plan === 'pro') return 'Pro';
  if (plan === 'business') return 'Business';
  return String(plan);
}

function formatAdminShippingAddress(u) {
  if (!u) return '—';
  const parts = [];
  if (u.shippingLine1) parts.push(u.shippingLine1);
  if (u.shippingLine2) parts.push(u.shippingLine2);
  const cityLine = [u.shippingCity, u.shippingState, u.shippingZipcode].filter(Boolean).join(', ');
  if (cityLine) parts.push(cityLine);
  if (u.shippingCountry) parts.push(u.shippingCountry);
  if (parts.length === 0) return '—';
  return parts.join('\n');
}

const AdminPanel = () => {
  const [userRows, setUserRows] = useState([]);
  const [userPagination, setUserPagination] = useState(null);
  const [userPage, setUserPage] = useState(1);
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [usersTableLoading, setUsersTableLoading] = useState(false);
  const [userListRefresh, setUserListRefresh] = useState(0);
  const prevUserQ = useRef('');
  const lastUserFetchKey = useRef('');
  const [allUsersForNfc, setAllUsersForNfc] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [tab, setTab] = useState(0);
  const [userSearch, setUserSearch] = useState('');

  // NFC state
  const [nfcList, setNfcList] = useState([]);
  const [nfcPage, setNfcPage] = useState(1);
  const [nfcPagination, setNfcPagination] = useState(null);
  const [nfcTableLoading, setNfcTableLoading] = useState(false);
  const [nfcListRefresh, setNfcListRefresh] = useState(0);
  const lastNfcFetchKey = useRef('');
  const [cardsByUser, setCardsByUser] = useState({});
  const [editingRow, setEditingRow] = useState(-1);
  const [bulkUids, setBulkUids] = useState('');
  const [bulkAssignCsv, setBulkAssignCsv] = useState('');

  // QR state
  const [qrCodes, setQrCodes] = useState([]);
  const [qrStats, setQrStats] = useState({});
  const [qrSearch, setQrSearch] = useState('');
  const [debouncedQrSearch, setDebouncedQrSearch] = useState('');
  const [qrPage, setQrPage] = useState(1);
  const [qrPagination, setQrPagination] = useState(null);
  const [qrTableLoading, setQrTableLoading] = useState(false);
  const [qrListRefresh, setQrListRefresh] = useState(0);
  const prevQrQ = useRef('');
  const lastQrFetchKey = useRef('');
  const lastSubFetchKey = useRef('');

  // Subscription state
  const [subscriptions, setSubscriptions] = useState([]);
  const [subStats, setSubStats] = useState({});
  const [subSearch, setSubSearch] = useState('');
  const [subQuery, setSubQuery] = useState('');
  const [subStatusFilter, setSubStatusFilter] = useState('');
  const [subPage, setSubPage] = useState(1);
  const [subPagination, setSubPagination] = useState(null);
  const [subTableLoading, setSubTableLoading] = useState(false);
  const [subTxnRefresh, setSubTxnRefresh] = useState(0);

  // Dialog state
  const [addDialog, setAddDialog] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_USER_FORM);
  const [addLoading, setAddLoading] = useState(false);

  const [viewDialog, setViewDialog] = useState(false);
  const [viewUser, setViewUser] = useState(null);
  const [viewCards, setViewCards] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);

  const [editDialog, setEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [nfcTapsDialog, setNfcTapsDialog] = useState({ open: false, uid: '', taps: [], loading: false });

  const toast = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // ── Data fetching ──
  const fetchAdminStats = async () => {
    const statsResp = await fetch(`${API}/cards/admin/stats`, { headers: authHeaders() });
    if (!statsResp.ok) throw new Error('Failed to fetch stats');
    const statsData = await statsResp.json();
    if (statsData.success) setStats(statsData.stats);
  };

  const fetchAllUsersForNfc = async () => {
    try {
      const resp = await fetch(`${API}/cards/admin/users`, { headers: authHeaders() });
      const data = await resp.json();
      if (data.success) setAllUsersForNfc(data.users || []);
    } catch (e) { console.error(e); }
  };

  const fetchAdminData = async () => {
    try {
      setLoading(true); setError(null);
      await fetchAdminStats();
    } catch (err) {
      setError(err.message);
    } finally {
      lastUserFetchKey.current = '';
      setLoading(false);
    }
  };

  const refreshUsersAndStats = async () => {
    try { await fetchAdminStats(); } catch {}
    lastUserFetchKey.current = '';
    setUserListRefresh((r) => r + 1);
    fetchAllUsersForNfc();
  };

  const bumpNfcList = () => setNfcListRefresh((r) => r + 1);

  const fetchUserCards = async (userId) => {
    if (!userId || cardsByUser[userId]) return;
    try {
      const resp = await fetch(`${API}/nfc/admin/users/${userId}/cards`, { headers: authHeaders() });
      const json = await resp.json();
      setCardsByUser((prev) => ({ ...prev, [userId]: json.cards || [] }));
    } catch (e) { console.error(e); }
  };

  const applySubSearch = () => {
    setSubQuery(subSearch.trim());
    setSubPage(1);
  };

  const fetchQrStats = async () => {
    try {
      const statsResp = await fetch(`${API}/qr/admin/stats`, { headers: authHeaders() });
      const statsData = await statsResp.json();
      if (statsData.success) setQrStats(statsData.stats || {});
    } catch (e) { console.error('Fetch QR stats error:', e); }
  };

  const refreshQrData = async () => {
    await fetchQrStats();
    setQrListRefresh((r) => r + 1);
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [userSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQrSearch(qrSearch.trim()), 400);
    return () => clearTimeout(t);
  }, [qrSearch]);

  useEffect(() => { fetchAdminData(); }, []);

  useEffect(() => {
    if (loading) return;
    const q = debouncedUserSearch;
    const searchChanged = prevUserQ.current !== q;
    prevUserQ.current = q;
    const p = searchChanged ? 1 : userPage;
    if (searchChanged && userPage !== 1) setUserPage(1);

    const key = `${p}:${q}:${userListRefresh}`;
    if (key === lastUserFetchKey.current) return;
    lastUserFetchKey.current = key;

    (async () => {
      try {
        setUsersTableLoading(true);
        const params = new URLSearchParams({ page: String(p), limit: String(USER_PAGE_SIZE) });
        if (q) params.set('q', q);
        const usersResp = await fetch(`${API}/cards/admin/users?${params}`, { headers: authHeaders() });
        if (!usersResp.ok) throw new Error('Failed to fetch users');
        const usersData = await usersResp.json();
        if (!usersData.success) return;
        let rows = usersData.users || [];
        let pag = usersData.pagination;
        let resolvedPage = p;
        if (p > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
          resolvedPage = Math.max(1, p - 1);
          setUserPage(resolvedPage);
          const params2 = new URLSearchParams({ page: String(resolvedPage), limit: String(USER_PAGE_SIZE) });
          if (q) params2.set('q', q);
          const r2 = await fetch(`${API}/cards/admin/users?${params2}`, { headers: authHeaders() });
          const d2 = await r2.json();
          rows = d2.users || [];
          pag = d2.pagination;
        }
        setUserRows(rows);
        setUserPagination(pag || null);
        setUserPage(resolvedPage);
      } catch (e) {
        console.error(e);
      } finally {
        setUsersTableLoading(false);
      }
    })();
  }, [loading, userPage, debouncedUserSearch, userListRefresh]);

  useEffect(() => {
    if (tab === 1) fetchAllUsersForNfc();
  }, [tab]);

  useEffect(() => {
    if (tab !== 1) {
      lastNfcFetchKey.current = '';
      return;
    }
    const p = nfcPage;
    const key = `${p}:${nfcListRefresh}`;
    if (key === lastNfcFetchKey.current) return;
    lastNfcFetchKey.current = key;

    (async () => {
      try {
        setNfcTableLoading(true);
        const params = new URLSearchParams({ page: String(p), limit: String(NFC_PAGE_SIZE) });
        const resp = await fetch(`${API}/nfc/admin/cards?${params}`, { headers: authHeaders() });
        if (!resp.ok) throw new Error('Failed to fetch NFC cards');
        const json = await resp.json();
        let rows = json.cards || [];
        let pag = json.pagination;
        let resolvedPage = p;
        if (p > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
          resolvedPage = Math.max(1, p - 1);
          setNfcPage(resolvedPage);
          const params2 = new URLSearchParams({ page: String(resolvedPage), limit: String(NFC_PAGE_SIZE) });
          const r2 = await fetch(`${API}/nfc/admin/cards?${params2}`, { headers: authHeaders() });
          const d2 = await r2.json();
          rows = d2.cards || [];
          pag = d2.pagination;
        }
        setNfcList((rows || []).map((c) => ({
          ...c,
          assignUserId: c.owner_user_id || '',
          assignCardUrl: c.target_type === 'card' ? (c.target_value || '') : '',
        })));
        setNfcPagination(pag || null);
        setNfcPage(resolvedPage);
      } catch (e) {
        console.error('Fetch NFC error:', e);
      } finally {
        setNfcTableLoading(false);
      }
    })();
  }, [tab, nfcPage, nfcListRefresh]);

  useEffect(() => {
    setEditingRow(-1);
  }, [nfcPage]);
  useEffect(() => {
    if (tab !== 2) {
      lastSubFetchKey.current = '';
      return;
    }
    const key = `${subPage}:${subQuery}:${subStatusFilter}:${subTxnRefresh}`;
    if (key === lastSubFetchKey.current) return;
    lastSubFetchKey.current = key;

    (async () => {
      try {
        setSubTableLoading(true);
        const params = new URLSearchParams({ page: String(subPage), limit: String(USER_PAGE_SIZE) });
        if (subQuery) params.set('search', subQuery);
        if (subStatusFilter) params.set('status', subStatusFilter);
        const [subsResp, statsResp] = await Promise.all([
          fetch(`${API}/payments/admin/subscriptions?${params}`, { headers: authHeaders() }),
          fetch(`${API}/payments/admin/stats`, { headers: authHeaders() }),
        ]);
        const subsData = await subsResp.json();
        const statsData = await statsResp.json();
        if (statsData.success) setSubStats(statsData.stats || {});
        if (!subsData.success) return;
        let rows = subsData.transactions || [];
        let pag = subsData.pagination;
        let resolvedPage = subPage;
        if (subPage > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
          resolvedPage = Math.max(1, subPage - 1);
          setSubPage(resolvedPage);
          const params2 = new URLSearchParams({ page: String(resolvedPage), limit: String(USER_PAGE_SIZE) });
          if (subQuery) params2.set('search', subQuery);
          if (subStatusFilter) params2.set('status', subStatusFilter);
          const r2 = await fetch(`${API}/payments/admin/subscriptions?${params2}`, { headers: authHeaders() });
          const d2 = await r2.json();
          if (d2.success) {
            rows = d2.transactions || [];
            pag = d2.pagination;
          }
        }
        setSubscriptions(rows);
        setSubPagination(pag || null);
        setSubPage(resolvedPage);
      } catch (e) {
        console.error(e);
      } finally {
        setSubTableLoading(false);
      }
    })();
  }, [tab, subPage, subQuery, subStatusFilter, subTxnRefresh]);

  useEffect(() => {
    if (tab !== 3) return;
    fetchQrStats();
  }, [tab]);

  useEffect(() => {
    if (tab !== 3) {
      lastQrFetchKey.current = '';
      return;
    }
    const q = debouncedQrSearch;
    const searchChanged = prevQrQ.current !== q;
    prevQrQ.current = q;
    const p = searchChanged ? 1 : qrPage;
    if (searchChanged && qrPage !== 1) setQrPage(1);

    const key = `${p}:${q}:${qrListRefresh}`;
    if (key === lastQrFetchKey.current) return;
    lastQrFetchKey.current = key;

    (async () => {
      try {
        setQrTableLoading(true);
        const params = new URLSearchParams({ page: String(p), limit: String(QR_PAGE_SIZE) });
        if (q) params.set('q', q);
        const codesResp = await fetch(`${API}/qr/admin/all?${params}`, { headers: authHeaders() });
        if (!codesResp.ok) throw new Error('Failed to fetch QR codes');
        const codesData = await codesResp.json();
        if (!codesData.success) return;
        let rows = codesData.codes || [];
        let pag = codesData.pagination;
        let resolvedPage = p;
        if (p > 1 && rows.length === 0 && pag && (pag.total || 0) > 0) {
          resolvedPage = Math.max(1, p - 1);
          setQrPage(resolvedPage);
          const params2 = new URLSearchParams({ page: String(resolvedPage), limit: String(QR_PAGE_SIZE) });
          if (q) params2.set('q', q);
          const r2 = await fetch(`${API}/qr/admin/all?${params2}`, { headers: authHeaders() });
          const d2 = await r2.json();
          rows = d2.codes || [];
          pag = d2.pagination;
        }
        setQrCodes(rows);
        setQrPagination(pag || null);
        setQrPage(resolvedPage);
      } catch (e) {
        console.error(e);
      } finally {
        setQrTableLoading(false);
      }
    })();
  }, [tab, qrPage, debouncedQrSearch, qrListRefresh]);

  // ── User CRUD actions ──
  const handleAddUser = async () => {
    if (!addForm.firstName || !addForm.email || !addForm.password) {
      toast('First name, email, and password are required', 'error'); return;
    }
    try {
      setAddLoading(true);
      const resp = await fetch(`${API}/cards/admin/users`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(addForm),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed');
      toast('User created successfully');
      setAddDialog(false); setAddForm(EMPTY_USER_FORM); refreshUsersAndStats();
    } catch (e) { toast(e.message, 'error'); }
    finally { setAddLoading(false); }
  };

  const handleViewUser = async (userId) => {
    try {
      setViewLoading(true); setViewDialog(true);
      const resp = await fetch(`${API}/cards/admin/users/${userId}`, { headers: authHeaders() });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed');
      setViewUser(data.user);
      setViewCards(data.cards || []);
    } catch (e) { toast(e.message, 'error'); setViewDialog(false); }
    finally { setViewLoading(false); }
  };

  const handleOpenEdit = async (userId) => {
    try {
      setEditLoading(true); setEditDialog(true);
      const resp = await fetch(`${API}/cards/admin/users/${userId}`, { headers: authHeaders() });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed');
      setEditForm({
        id: data.user.id, firstName: data.user.firstName, lastName: data.user.lastName,
        email: data.user.email, role: data.user.role, isActive: data.user.isActive,
      });
    } catch (e) { toast(e.message, 'error'); setEditDialog(false); }
    finally { setEditLoading(false); }
  };

  const handleSaveEdit = async () => {
    try {
      setEditLoading(true);
      const resp = await fetch(`${API}/cards/admin/users/${editForm.id}`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({
          firstName: editForm.firstName, lastName: editForm.lastName,
          email: editForm.email, role: editForm.role, isActive: editForm.isActive,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed');
      toast('User updated successfully');
      setEditDialog(false); refreshUsersAndStats();
    } catch (e) { toast(e.message, 'error'); }
    finally { setEditLoading(false); }
  };

  const handleDeleteUser = async () => {
    try {
      setDeleteLoading(true);
      const resp = await fetch(`${API}/cards/admin/users/${deleteDialog.user.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.message || 'Failed');
      toast('User deleted successfully');
      setDeleteDialog({ open: false, user: null }); refreshUsersAndStats();
    } catch (e) { toast(e.message, 'error'); }
    finally { setDeleteLoading(false); }
  };

  // ── NFC handlers ──
  const handleBulkAddUids = async () => {
    try {
      const uids = bulkUids.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
      if (!uids.length) return;
      const resp = await fetch(`${API}/nfc/cards/bulk`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ uids }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.message || 'Failed');
      toast(`Inserted ${j.inserted}`); setBulkUids(''); setNfcPage(1); bumpNfcList();
    } catch (e) { toast(e.message, 'error'); }
  };

  const handleBulkAssign = async () => {
    try {
      const resp = await fetch(`${API}/nfc/admin/assign-bulk`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ csv: bulkAssignCsv }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.message || 'Failed');
      toast(`Assigned ${j.ok}, failed ${j.fail}`, j.fail ? 'warning' : 'success'); setNfcPage(1); bumpNfcList();
    } catch (e) { toast(e.message, 'error'); }
  };

  const openNfcTaps = async (uid) => {
    setNfcTapsDialog({ open: true, uid, taps: [], loading: true });
    try {
      const resp = await fetch(`${API}/nfc/cards/${uid}/taps`, { headers: authHeaders() });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.message || 'Failed to load taps');
      setNfcTapsDialog({ open: true, uid, taps: j.taps || [], loading: false });
    } catch (e) {
      toast(e.message, 'error');
      setNfcTapsDialog({ open: false, uid: '', taps: [], loading: false });
    }
  };

  const handleNfcSaveRow = async (idx) => {
    const n = nfcList[idx];
    try {
      if (!n.assignUserId) return;
      const resp = await fetch(`${API}/nfc/cards/${n.uid}/assign`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ userId: Number(n.assignUserId), cardUrl: n.assignCardUrl || undefined }),
      });
      const j = await resp.json();
      if (!resp.ok) throw new Error(j.message || 'Failed');
      toast('Assigned successfully'); setEditingRow(-1); bumpNfcList();
    } catch (e) { toast(e.message, 'error'); }
  };

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchAdminData} startIcon={<RefreshIcon />}>Retry</Button>
        } sx={{ borderRadius: 1 }}>{error}</Alert>
      </Box>
    );
  }

  const kpis = [
    { value: stats.totalUsers, label: 'Total Users', sub: `+${stats.monthlyUsers || 0} this month`, color: PRIMARY },
    { value: stats.totalCards, label: 'Total Cards', sub: `+${stats.monthlyCards || 0} this month`, color: '#10B981' },
    { value: stats.activeSessions, label: 'Active Sessions', sub: `+${stats.hourlySessions || 0} this hour`, color: '#F59E0B' },
    { value: stats.adminActions, label: 'Admin Actions', sub: `+${stats.dailyActions || 0} today`, color: '#8B5CF6' },
  ];

  return (
    <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', mb: 4, gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Admin Panel</Typography>
          <Typography variant="body2" color="text.secondary">Manage users, cards, and system settings</Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={fetchAdminData} disabled={loading}
          sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, px: 2 }}>Refresh</Button>
      </Box>

      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 4 }}>
        {kpis.map((kpi, i) => (
          <Card key={i} sx={{ boxShadow: CARD_SHADOW, borderRadius: 1, border: '1px solid', borderColor: 'divider', borderLeft: `4px solid ${kpi.color}`, bgcolor: `${kpi.color}0A` }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              {loading ? (
                <>
                  <Skeleton variant="text" width="45%" height={40} animation="wave" sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="65%" height={22} animation="wave" sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width="55%" height={20} animation="wave" />
                </>
              ) : (
                <>
                  <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{kpi.value?.toLocaleString() || '0'}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, fontSize: '13px' }}>{kpi.label}</Typography>
                  <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>{kpi.sub}</Typography>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(e, v) => setTab(v)}
        sx={{ mb: 3, minHeight: 42, '& .MuiTabs-indicator': { height: 2 },
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '14px', minHeight: 42, px: 2, mr: 1 },
        }}>
        <Tab label="Users" />
        <Tab label="NFC Cards" />
        <Tab label="Subscriptions" />
        <Tab label="QR Codes" />
      </Tabs>

      {/* ═══════════ USERS TAB ═══════════ */}
      {tab === 0 && (
        <Card sx={{ ...cardBase, overflow: 'hidden' }}>
          <Box sx={sectionHeaderSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>User Management</Typography>
              <TextField size="small" placeholder="Search users…" value={userSearch}
                onChange={(e) => {
                  const v = e.target.value;
                  setUserSearch(v);
                  if (!v.trim()) setDebouncedUserSearch('');
                }}
                sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
              />
            </Box>
            <Button variant="contained" size="small" startIcon={<AddIcon />}
              onClick={() => { setAddForm(EMPTY_USER_FORM); setAddDialog(true); }}
              sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}>
              Add User
            </Button>
          </Box>
          <TableContainer
            sx={
              userPagination && userPagination.totalPages > 1
                ? { minHeight: ADMIN_PAGINATED_TABLE_MIN_PX }
                : undefined
            }
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeadCellSx}>User</TableCell>
                  <TableCell sx={tableHeadCellSx}>Role</TableCell>
                  <TableCell sx={tableHeadCellSx}>Status</TableCell>
                  <TableCell align="center" sx={tableHeadCellSx}>Cards</TableCell>
                  <TableCell sx={tableHeadCellSx}>Last Login</TableCell>
                  <TableCell align="right" sx={tableHeadCellSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading || usersTableLoading ? (
                  <AdminUserTableSkeletonRows rows={USER_PAGE_SIZE} />
                ) : userRows.length > 0 ? (
                  userRows.map((user) => (
                    <TableRow key={user.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, bgcolor: PRIMARY, fontSize: '0.85rem', fontWeight: 700 }}>
                            {user.name?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary' }}>{user.name}</Typography>
                            <Typography
                              variant="caption"
                              sx={(t) => ({
                                color: t.palette.mode === 'dark' ? t.palette.grey[300] : t.palette.text.secondary,
                              })}
                            >
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={user.role}
                          size="small"
                          sx={(t) => ({
                            borderRadius: '12px',
                            fontSize: '12px',
                            height: 24,
                            fontWeight: 600,
                            bgcolor: user.role === 'admin' ? alpha(PRIMARY, 0.14) : (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.1) : t.palette.grey[100]),
                            color: user.role === 'admin' ? PRIMARY : 'text.primary',
                          })}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: user.status === 'active' ? '#10B981' : '#EF4444' }} />
                          <Typography variant="body2" sx={{ fontSize: '13px', color: user.status === 'active' ? '#10B981' : '#EF4444', fontWeight: 500 }}>
                            {user.status}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2, fontWeight: 600, fontSize: '14px', color: 'text.primary' }}>{user.cards}</TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>{user.lastLogin}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="View details" arrow>
                            <IconButton size="small" onClick={() => handleViewUser(user.id)}
                              sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }}>
                              <VisibilityIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit user" arrow>
                            <IconButton size="small" onClick={() => handleOpenEdit(user.id)}
                              sx={{ color: 'text.secondary', '&:hover': { color: '#F59E0B' } }}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete user" arrow>
                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, user })}
                              sx={{ color: 'text.secondary', '&:hover': { color: '#EF4444' } }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {debouncedUserSearch ? 'No users match your search.' : 'No users found.'}
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && !usersTableLoading && userPagination && userPagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Pagination
                color="primary"
                count={userPagination.totalPages}
                page={userPage}
                onChange={(_, p) => setUserPage(p)}
                shape="rounded"
              />
            </Box>
          )}
        </Card>
      )}

      {/* ═══════════ NFC TAB ═══════════ */}
      {tab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Card sx={cardBase}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, display: 'block' }}>Register New UIDs</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField size="small" placeholder="UID1, UID2, UID3…" value={bulkUids} onChange={(e) => setBulkUids(e.target.value)} fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }} />
                  <Button variant="contained" size="small" onClick={handleBulkAddUids} disabled={!bulkUids.trim()} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', px: 2.5, boxShadow: 'none' }}>Add</Button>
                </Box>
              </CardContent>
            </Card>
            <Card sx={cardBase}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, display: 'block' }}>Bulk Assign (CSV)</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField placeholder="uid, userId-or-email, cardUrl (one per line)" value={bulkAssignCsv} onChange={(e) => setBulkAssignCsv(e.target.value)} fullWidth multiline minRows={1} maxRows={3} size="small" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }} />
                  <Button variant="contained" size="small" onClick={handleBulkAssign} disabled={!bulkAssignCsv.trim()} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', px: 2.5, boxShadow: 'none' }}>Assign</Button>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Card sx={{ ...cardBase, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>All NFC Cards</Typography>
                {nfcPagination != null && (
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                    {nfcPagination.total.toLocaleString('en-IN')} card{nfcPagination.total !== 1 ? 's' : ''} registered
                  </Typography>
                )}
              </Box>
            </Box>
            <TableContainer
              sx={
                nfcPagination && nfcPagination.totalPages > 1
                  ? { minHeight: ADMIN_PAGINATED_TABLE_MIN_PX }
                  : undefined
              }
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>UID</TableCell>
                    <TableCell sx={tableHeadCellSx}>Status</TableCell>
                    <TableCell sx={tableHeadCellSx}>Owner</TableCell>
                    <TableCell sx={tableHeadCellSx}>Target</TableCell>
                    <TableCell align="center" sx={tableHeadCellSx}>Taps</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nfcTableLoading ? (
                    <AdminNfcTableSkeletonRows rows={NFC_PAGE_SIZE} />
                  ) : nfcList.length > 0 ? (
                  nfcList.map((n, idx) => {
                    const owner = allUsersForNfc.find(x => x.id === n.owner_user_id);
                    const shortUid = n.uid.length > 16 ? `${n.uid.slice(0, 8)}…${n.uid.slice(-6)}` : n.uid;
                    return (
                      <TableRow key={n.uid} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Tooltip title={n.uid} arrow>
                            <Typography
                              variant="body2"
                              component="span"
                              sx={(t) => {
                                const dark = t.palette.mode === 'dark';
                                return {
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  display: 'inline-block',
                                  color: t.palette.text.primary,
                                  border: '1px solid',
                                  borderColor: t.palette.divider,
                                  bgcolor: dark ? alpha(PRIMARY, 0.18) : t.palette.grey[100],
                                };
                              }}
                            >
                              {shortUid}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: n.is_active ? '#10B981' : '#9CA3AF' }} />
                            <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: n.is_active ? '#10B981' : 'text.secondary' }}>{n.is_active ? 'Active' : 'Inactive'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {editingRow === idx ? (
                            <TextField select size="small" value={n.assignUserId ?? ''} onChange={(e) => { const val = e.target.value; setNfcList(prev => prev.map((p, i) => i === idx ? { ...p, assignUserId: val, assignCardUrl: '' } : p)); fetchUserCards(val); }} sx={{ width: 200, '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }} displayEmpty>
                              <MenuItem value="" disabled><em>Select user</em></MenuItem>
                              {allUsersForNfc.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                            </TextField>
                          ) : (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: owner ? 'text.primary' : 'text.disabled' }}>{owner ? owner.name : 'Unassigned'}</Typography>
                              {owner && (
                                <Typography variant="caption" sx={(t) => ({ color: t.palette.mode === 'dark' ? t.palette.grey[300] : t.palette.text.secondary })}>
                                  {owner.email}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {editingRow === idx ? (
                            <TextField select size="small" value={n.assignCardUrl ?? ''} onChange={(e) => setNfcList(prev => prev.map((p, i) => i === idx ? { ...p, assignCardUrl: e.target.value } : p))} disabled={!n.assignUserId} sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }} displayEmpty>
                              <MenuItem value="" disabled><em>Select card</em></MenuItem>
                              {(cardsByUser[n.assignUserId] || []).map((c) => <MenuItem key={c.cardUrl} value={c.cardUrl}>{c.cardUrl}</MenuItem>)}
                            </TextField>
                          ) : n.target_value ? (
                            <Chip label={n.target_value} size="small" sx={{ borderRadius: '12px', fontSize: '12px', height: 24, fontWeight: 500, bgcolor: `${PRIMARY}14`, color: PRIMARY }} />
                          ) : <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.disabled' }}>—</Typography>}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', color: 'text.primary' }}>{n.tap_count || 0}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                            {editingRow === idx ? (<>
                              <Button size="small" variant="contained" onClick={() => handleNfcSaveRow(idx)} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, fontSize: '12px', boxShadow: 'none' }}>Save</Button>
                              <Button size="small" onClick={() => { setEditingRow(-1); bumpNfcList(); }} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600, fontSize: '12px', color: 'text.secondary' }}>Cancel</Button>
                            </>) : (<>
                              <Tooltip title="Edit assignment" arrow><IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }} onClick={() => { setEditingRow(idx); fetchUserCards(n.owner_user_id || allUsersForNfc[0]?.id); }}><EditIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                              <Tooltip title="View taps" arrow><IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }} onClick={() => openNfcTaps(n.uid)}><VisibilityIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            </>)}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                  ) : (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>No NFC cards registered yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!nfcTableLoading && nfcPagination && nfcPagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Pagination
                  color="primary"
                  count={nfcPagination.totalPages}
                  page={nfcPage}
                  onChange={(_, p) => setNfcPage(p)}
                  shape="rounded"
                />
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* ═══════════ SUBSCRIPTIONS TAB ═══════════ */}
      {tab === 2 && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: 'Total Revenue', value: `₹${(subStats.totalRevenue || 0).toLocaleString()}`, color: '#10B981' },
              { label: 'This Month', value: `₹${(subStats.monthlyRevenue || 0).toLocaleString()}`, color: PRIMARY },
              { label: 'Active Subs', value: subStats.activeSubscriptions || 0, color: '#8B5CF6' },
              { label: 'Pro / Business / Lifetime Access / Free', value: `${subStats.proUsers || 0} / ${subStats.businessUsers || 0} / ${subStats.lifetimeUsers || 0} / ${subStats.freeUsers || 0}`, color: '#F59E0B' },
            ].map((kpi, i) => (
              <Card key={i} sx={{ ...cardBase, borderLeft: `4px solid ${kpi.color}`, bgcolor: `${kpi.color}0A` }}>
                <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{kpi.value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mt: 0.5 }}>{kpi.label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card sx={{ ...cardBase, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>All Transactions</Typography>
                {subPagination != null && (
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                    {subPagination.total.toLocaleString('en-IN')} transaction{subPagination.total !== 1 ? 's' : ''}{subQuery ? ' matching search' : ''}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" placeholder="Search by email, name, payment ID..." value={subSearch} onChange={(e) => setSubSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applySubSearch(); }} sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 1 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }} />
                <TextField
                  select
                  size="small"
                  value={subStatusFilter}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSubStatusFilter(val);
                    setSubPage(1);
                  }}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected) => subscriptionStatusFilterLabel(selected),
                  }}
                  sx={{
                    minWidth: 150,
                    '& .MuiOutlinedInput-root': { borderRadius: 1 },
                    '& .MuiSelect-select': {
                      color: 'text.primary',
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {SUBSCRIPTION_FILTER_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{subscriptionStatusFilterLabel(s)}</MenuItem>
                  ))}
                </TextField>
                <Button size="small" variant="outlined" onClick={applySubSearch} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>Search</Button>
              </Box>
            </Box>
            <TableContainer
              sx={
                subPagination && subPagination.totalPages > 1
                  ? { minHeight: ADMIN_SUB_TXN_TABLE_MIN_PX }
                  : undefined
              }
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['User','Plan','Amount','Status','Payment ID','Started','Expires','Actions'].map(h => <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subTableLoading ? (
                    <AdminSubTransactionsSkeletonRows rows={USER_PAGE_SIZE} />
                  ) : subscriptions.map((sub) => {
                    const isOrder = sub.tx_source === 'order';
                    // Order rows: razorpay_orders.plan_type (e.g. lifetime). Subscription rows: subscriptions.plan_type (pro/business) — NOT users.plan (current account), or every row shows "Lifetime" after they upgrade.
                    const planCode = isOrder
                      ? (sub.plan_type || 'lifetime')
                      : (sub.plan_type || sub.user_plan || 'free');
                    const planLabel = formatUserPlanLabel(planCode);
                    const paidPlan = ['pro', 'business', 'premium', 'lifetime'].includes(planCode);
                    const statusColor = { active: '#10B981', charged: '#10B981', authenticated: '#3B82F6', created: '#94A3B8', cancelled: '#EF4444', halted: '#EF4444', expired: '#94A3B8', failed: '#EF4444', completed: '#8B5CF6', paid: '#10B981' }[sub.status] || '#94A3B8';
                    return (
                      <TableRow key={`${sub.tx_source || 'subscription'}-${sub.id}`} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px', color: 'text.primary' }}>{sub.first_name} {sub.last_name}</Typography>
                          <Typography variant="caption" sx={(t) => ({ color: t.palette.mode === 'dark' ? t.palette.grey[300] : t.palette.text.secondary })}>{sub.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planLabel}
                            size="small"
                            sx={(t) => ({
                              bgcolor: planCode === 'lifetime' ? '#ca8a0418' : paidPlan ? alpha(PRIMARY, 0.12) : (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.1) : t.palette.grey[100]),
                              color: planCode === 'lifetime' ? '#b45309' : paidPlan ? PRIMARY : 'text.primary',
                              fontWeight: 600,
                              fontSize: '11px',
                              height: 22,
                            })}
                          />
                        </TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>₹{(sub.amount / 100).toLocaleString()}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor }} />
                            <Typography variant="body2" sx={{ fontSize: '12px', textTransform: 'capitalize' }}>{sub.status}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontSize: '11px', color: 'text.secondary', fontFamily: 'monospace' }}>{sub.razorpay_payment_id || (isOrder && sub.razorpay_order_id ? `order:${sub.razorpay_order_id}` : '—')}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontSize: '12px' }}>{sub.starts_at ? fmtDate(sub.starts_at) : '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontSize: '12px' }}>{sub.expires_at ? fmtDate(sub.expires_at) : '—'}</Typography></TableCell>
                        <TableCell>
                          <Tooltip title={['pro','business','premium','lifetime'].includes(sub.user_plan) ? 'Downgrade to free' : 'Upgrade to pro'}>
                            <IconButton size="small" onClick={async () => {
                              const newPlan = ['pro','business','premium','lifetime'].includes(sub.user_plan) ? 'free' : 'pro';
                              try {
                                await fetch(`${API}/payments/admin/toggle-plan`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ userId: sub.user_id, plan: newPlan }) });
                                toast(`User set to ${newPlan}`); setSubTxnRefresh((r) => r + 1);
                              } catch { toast('Failed to update plan', 'error'); }
                            }} sx={{ color: 'text.disabled', '&:hover': { color: PRIMARY } }}>
                              <CreditCardIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!subTableLoading && subscriptions.length === 0 && (
                    <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      {subQuery || subStatusFilter ? 'No transactions match your filters.' : 'No transactions found.'}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!subTableLoading && subPagination && subPagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Pagination
                  color="primary"
                  count={subPagination.totalPages}
                  page={subPage}
                  onChange={(_, p) => setSubPage(p)}
                  shape="rounded"
                />
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* ═══════════ QR CODES TAB ═══════════ */}
      {tab === 3 && (
        <Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: 'Total QR Codes', value: (qrStats.totalQR || 0).toLocaleString('en-IN'), color: PRIMARY },
              { label: 'Total Scans', value: (qrStats.totalScans || 0).toLocaleString('en-IN'), color: '#10B981' },
              { label: 'Active QR Codes', value: (qrStats.activeQR || 0).toLocaleString('en-IN'), color: '#8B5CF6' },
              { label: 'Users with QR', value: (qrStats.qrUsers || 0).toLocaleString('en-IN'), color: '#F59E0B' },
              { label: 'Paid Analytics Users', value: (qrStats.paidUsers || 0).toLocaleString('en-IN'), color: '#EF4444' },
            ].map((kpi, i) => (
              <Card key={i} sx={{ ...cardBase, borderLeft: `4px solid ${kpi.color}`, bgcolor: `${kpi.color}0A` }}>
                <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>{kpi.value}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '12px', mt: 0.5 }}>{kpi.label}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card sx={{ ...cardBase, overflow: 'hidden' }}>
            <Box sx={sectionHeaderSx}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>All QR Codes</Typography>
                {qrPagination != null && (
                  <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                    {qrPagination.total.toLocaleString('en-IN')} code{qrPagination.total !== 1 ? 's' : ''}{debouncedQrSearch ? ' matching search' : ''}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <TextField size="small" placeholder="Search by label, URL, user..." value={qrSearch}
                  onChange={(e) => setQrSearch(e.target.value)}
                  sx={{ width: 260, '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: '13px' } }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }} />
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                  onClick={refreshQrData} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 600 }}>Refresh</Button>
              </Box>
            </Box>
            <TableContainer
              sx={
                qrPagination && qrPagination.totalPages > 1
                  ? { minHeight: ADMIN_PAGINATED_TABLE_MIN_PX }
                  : undefined
              }
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {['Label','Destination URL','User','Scans','Active','Created','QR Link'].map(h => (
                      <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrTableLoading ? (
                    <AdminQrTableSkeletonRows rows={QR_PAGE_SIZE} />
                  ) : qrCodes.length > 0 ? (
                    qrCodes.map((q) => (
                    <TableRow key={q.uid} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>{q.label || '—'}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{q.uid.slice(0, 12)}...</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={q.target_url}>
                          <Typography variant="body2" sx={{ fontSize: '13px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1976d2', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => window.open(q.target_url, '_blank')}>
                            {q.target_url}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13px' }}>{q.first_name} {q.last_name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{q.email}</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Chip
                          label={(q.scan_count || 0).toLocaleString('en-IN')}
                          size="small"
                          sx={(t) => ({
                            fontWeight: 600,
                            fontSize: '12px',
                            height: 22,
                            bgcolor: q.scan_count > 0 ? alpha(PRIMARY, 0.15) : (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.1) : t.palette.grey[100]),
                            color: q.scan_count > 0 ? PRIMARY : 'text.primary',
                          })}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: q.is_active ? '#10B981' : '#EF4444', mx: 'auto' }} />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" sx={{ fontSize: '13px', color: 'text.secondary' }}>{fmtDate(q.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Tooltip title={`https://app.webcard.digital/qr/${q.uid}`}>
                          <IconButton size="small" onClick={() => window.open(`https://app.webcard.digital/qr/${q.uid}`, '_blank')} sx={{ color: PRIMARY }}>
                            <OpenIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {debouncedQrSearch ? 'No QR codes match your search.' : 'No QR codes found.'}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!qrTableLoading && qrPagination && qrPagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Pagination
                  color="primary"
                  count={qrPagination.totalPages}
                  page={qrPage}
                  onChange={(_, p) => setQrPage(p)}
                  shape="rounded"
                />
              </Box>
            )}
          </Card>
        </Box>
      )}

      {/* ═══════════ ADD USER DIALOG ═══════════ */}
      <Dialog open={addDialog} onClose={() => setAddDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Add New User</Typography>
          <IconButton size="small" onClick={() => setAddDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="First Name" value={addForm.firstName} required size="small"
                onChange={(e) => setAddForm(p => ({ ...p, firstName: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Last Name" value={addForm.lastName} size="small"
                onChange={(e) => setAddForm(p => ({ ...p, lastName: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" value={addForm.email} required size="small"
                onChange={(e) => setAddForm(p => ({ ...p, email: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Password" type="password" value={addForm.password} required size="small"
                onChange={(e) => setAddForm(p => ({ ...p, password: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Role" select value={addForm.role} size="small"
                onChange={(e) => setAddForm(p => ({ ...p, role: e.target.value }))}>
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setAddDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUser} disabled={addLoading}
            startIcon={addLoading ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}>Create User</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ VIEW USER DIALOG ═══════════ */}
      <Dialog open={viewDialog} onClose={() => { setViewDialog(false); setViewUser(null); setViewCards([]); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>User Details</Typography>
          <IconButton size="small" onClick={() => setViewDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : viewUser && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: PRIMARY, fontSize: '1.3rem', fontWeight: 700 }}>
                  {viewUser.firstName?.charAt(0)}{viewUser.lastName?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, color: 'text.primary' }}>
                    {viewUser.firstName} {viewUser.lastName}
                  </Typography>
                  <Typography variant="body2" sx={(t) => ({ color: t.palette.mode === 'dark' ? t.palette.grey[300] : t.palette.text.secondary })}>
                    {viewUser.email}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={viewUser.role}
                      size="small"
                      sx={(t) => ({
                        height: 22,
                        fontSize: '11px',
                        fontWeight: 600,
                        bgcolor: viewUser.role === 'admin' ? alpha(PRIMARY, 0.14) : (t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.1) : t.palette.grey[100]),
                        color: viewUser.role === 'admin' ? PRIMARY : 'text.primary',
                      })}
                    />
                    <Chip label={viewUser.isActive ? 'Active' : 'Inactive'} size="small" sx={{ height: 22, fontSize: '11px', fontWeight: 600, bgcolor: viewUser.isActive ? '#10B98114' : '#EF444414', color: viewUser.isActive ? '#10B981' : '#EF4444' }} />
                    <Chip
                      label={formatUserPlanLabel(viewUser.plan)}
                      size="small"
                      sx={(t) => ({
                        height: 22,
                        fontSize: '11px',
                        fontWeight: 600,
                        bgcolor: t.palette.mode === 'dark' ? alpha(t.palette.common.white, 0.1) : t.palette.grey[100],
                        color: 'text.primary',
                      })}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { icon: <PersonIcon sx={{ fontSize: 16 }} />, label: 'Username', value: viewUser.username || '—' },
                  { icon: <CalendarIcon sx={{ fontSize: 16 }} />, label: 'Joined', value: viewUser.createdAt ? fmtDate(viewUser.createdAt) : '—' },
                  { icon: <EmailIcon sx={{ fontSize: 16 }} />, label: 'Email Verified', value: viewUser.emailVerified ? 'Yes' : 'No' },
                ].map((item, i) => (
                  <Grid item xs={4} key={i}>
                    <Box sx={adminInsetBoxSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, color: 'text.secondary' }}>
                        {item.icon}
                        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'text.secondary' }}>{item.label}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 0.5, color: 'text.primary' }}>
                Phone & shipping
              </Typography>
              <Box sx={{ ...adminInsetBoxSx, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'text.secondary', display: 'block', mb: 0.25 }}>
                      Mobile
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{viewUser.phone || '—'}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <HomeIcon sx={{ fontSize: 18, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', color: 'text.secondary', display: 'block', mb: 0.25 }}>
                      Address
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-line', wordBreak: 'break-word', color: 'text.primary' }}>
                      {formatAdminShippingAddress(viewUser)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
                Cards ({viewCards.length})
              </Typography>
              {viewCards.length > 0 ? viewCards.map(card => (
                <Box key={card.id} sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  p: 1.5, mb: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{card.card_url}</Typography>
                    <Typography variant="caption" color="text.secondary">{card.template_name} · {fmtDate(card.created_at)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Chip
                      label={card.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '10px',
                        fontWeight: 600,
                        bgcolor: (t) => (card.is_active ? alpha('#10B981', 0.15) : alpha(t.palette.error.main, 0.14)),
                        color: card.is_active ? '#10B981' : '#EF4444',
                      }}
                    />
                    <Tooltip title="Open card" arrow>
                      <IconButton size="small" onClick={() => window.open(`https://app.webcard.digital/${card.card_url}`, '_blank')}
                        sx={{ color: 'text.secondary', '&:hover': { color: PRIMARY } }}>
                        <OpenIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No cards created yet</Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ═══════════ EDIT USER DIALOG ═══════════ */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2, bgcolor: 'background.paper' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Edit User</Typography>
          <IconButton size="small" onClick={() => setEditDialog(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {editLoading && !editForm.id ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={6}>
                <TextField fullWidth label="First Name" value={editForm.firstName || ''} size="small"
                  onChange={(e) => setEditForm(p => ({ ...p, firstName: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Last Name" value={editForm.lastName || ''} size="small"
                  onChange={(e) => setEditForm(p => ({ ...p, lastName: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Email" type="email" value={editForm.email || ''} size="small"
                  onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Role" select value={editForm.role || 'user'} size="small"
                  onChange={(e) => setEditForm(p => ({ ...p, role: e.target.value }))}>
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <FormControlLabel
                    control={<Switch checked={editForm.isActive ?? true} onChange={(e) => setEditForm(p => ({ ...p, isActive: e.target.checked }))} />}
                    label={<Typography variant="body2" sx={{ fontWeight: 500 }}>{editForm.isActive ? 'Active' : 'Inactive'}</Typography>}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditDialog(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading}
            startIcon={editLoading ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ NFC TAPS (admin) ═══════════ */}
      <Dialog open={nfcTapsDialog.open} onClose={() => setNfcTapsDialog({ open: false, uid: '', taps: [], loading: false })} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>NFC taps</Typography>
          <IconButton size="small" onClick={() => setNfcTapsDialog({ open: false, uid: '', taps: [], loading: false })}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 0 }}>
          {nfcTapsDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={36} sx={{ color: PRIMARY }} /></Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontFamily: 'monospace', fontSize: '12px' }}>{nfcTapsDialog.uid}</Typography>
              {nfcTapsDialog.taps.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No taps recorded yet.</Typography>
              ) : (
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeadCellSx}>When</TableCell>
                        <TableCell sx={tableHeadCellSx}>IP</TableCell>
                        <TableCell sx={tableHeadCellSx}>Country</TableCell>
                        <TableCell sx={tableHeadCellSx}>Referrer</TableCell>
                        <TableCell sx={tableHeadCellSx}>User agent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {nfcTapsDialog.taps.map((t) => (
                        <TableRow key={t.id} hover>
                          <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{fmtDateTime(t.created_at)}</TableCell>
                          <TableCell sx={{ fontSize: '12px', fontFamily: 'monospace' }}>{t.ip || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '12px' }}>{t.country || '—'}</TableCell>
                          <TableCell sx={{ fontSize: '12px', maxWidth: 140 }}><Tooltip title={t.referrer || ''}><Typography noWrap variant="inherit">{t.referrer || '—'}</Typography></Tooltip></TableCell>
                          <TableCell sx={{ fontSize: '11px', maxWidth: 220 }}><Tooltip title={t.user_agent || ''}><Typography noWrap variant="inherit">{t.user_agent || '—'}</Typography></Tooltip></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setNfcTapsDialog({ open: false, uid: '', taps: [], loading: false })} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ═══════════ DELETE USER DIALOG ═══════════ */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <WarningIcon sx={{ color: '#EF4444' }} />
          </Box>
          Delete User
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Are you sure you want to delete <strong>{deleteDialog.user?.name}</strong> ({deleteDialog.user?.email})?
            This will permanently remove their account and all {deleteDialog.user?.cards || 0} card(s).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1 }}>Delete User</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 1 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
